import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "@phosphor-icons/react";
import DismissibleIntro from "@/components/DismissibleIntro";

/**
 * SectionOnboarding — pattern riusabile di mini onboarding "Hai N strade".
 *
 * 3 card animate (staggered) con icone colorate, numero grande in sfondo,
 * CTA "Parti da qui →". Dismissibile via X (persistenza localStorage).
 *
 * Props:
 *   - storageKey: string · chiave univoca per persistenza dismiss
 *       (es. "foto-editor:strade", "annuncio:strade")
 *   - overline:   string · pre-titolo es. "Come migliorare le foto"
 *   - title:      JSX/string · titolo principale (può contenere markup)
 *   - subtitle:   string · paragrafo introduttivo sotto al titolo
 *   - steps:      Array<{ key, Icon, overline, title, body, accent, onClick? }>
 *       Ogni step è una card. Se passato `onClick`, la card è cliccabile.
 *       `accent` = HEX color usato per icona + numero in sfondo.
 *   - className:  classi extra opzionali sul wrapper
 *
 * Esempio:
 *   <SectionOnboarding
 *     storageKey="promuovi:3-canali"
 *     overline="Come promuovere l'annuncio"
 *     title={<>Hai <span className="italic-accent text-[#7C8B6E]">3 strade</span> · usale tutte</>}
 *     subtitle="Ogni canale raggiunge clienti diversi…"
 *     steps={[
 *       { key:"a", Icon: ShareNetwork, overline:"1 · Link",  title:"Pagina pubblica", body:"…", accent:"#7C8B6E", onClick: () => setTab("a") },
 *       …
 *     ]}
 *   />
 */
export default function SectionOnboarding({
  storageKey,
  overline,
  title,
  subtitle,
  steps,
  className = "",
}) {
  if (!Array.isArray(steps) || steps.length === 0) return null;

  // Grid columns auto: 2 step → 2 col, 3 step → 3 col, 4 step → 2x2.
  const gridCols =
    steps.length === 2 ? "sm:grid-cols-2" :
    steps.length === 4 ? "sm:grid-cols-2 lg:grid-cols-4" :
    "sm:grid-cols-3";

  return (
    <DismissibleIntro storageKey={storageKey} className={`mb-4 sm:mb-6 ${className}`}>
      <div className="bg-gradient-to-br from-[#F8F7F2] to-[#F1F4ED] border border-[#E5E7E0] rounded-xl p-2.5 sm:p-6">
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-center"
        >
          <span className="overline text-[10px] sm:text-[11px]">{overline}</span>
          {/* iter v29 r207 — Su iPhone il titolo `font-editorial text-xl`
              di default non era ENORME come pixel, ma il Cormorant Garamond
              + leading default occupa tanto verticalmente. text-lg/text-xl
              tiene la pagina più densa, intuitiva e leggibile.
              iter v29 r214 — User M1504: ulteriore riduzione mobile,
              compatti i testi guida sotto perché occupavano 70% schermo.
              iter v29 r221 — User M1547: «la guida è troppo grossa, la
              riduci ancora». Mobile: 14px + leading-tight, sub 11px.
              iter v29 r242 — User M1399: testi centrati per leggibilità. */}
          <h3 className="font-editorial text-[14px] sm:text-2xl leading-tight mt-1 mb-0.5">{title}</h3>
          {subtitle && (
            <p className="text-[11px] sm:text-[12.5px] text-[#6B6A65] leading-snug max-w-xl mx-auto">{subtitle}</p>
          )}
        </motion.div>

        <div className={`mt-2.5 sm:mt-5 grid grid-cols-1 ${gridCols} gap-2 sm:gap-2.5 relative`}>
          {steps.map(({ key, Icon, overline: stepOverline, title: stepTitle, body, accent, onClick }, idx) => {
            // iter v28 r370 — Bug fix mobile (User M1280):
            // su iOS Safari motion.button con whileHover non attivava il
            // click al primo tap (whileHover crea uno stato CSS :hover
            // sticky → primo tap = hover, secondo tap = click). L'utente
            // toccava "Parti da qui" e sembrava che non succedesse nulla.
            //
            // Fix: due cambiamenti:
            //   1. wrapper HTML <button> nativo (senza motion.button) che
            //      gestisce in modo standard il touch event.
            //   2. motion.div interno per le animazioni di entrata (fade
            //      + translate). Niente whileHover (= gli hover-effect
            //      sono CSS only via group-hover, che iOS gestisce bene).
            //   3. touch-action: manipulation per disabilitare il double-tap
            //      to zoom e ottenere risposta immediata al tap.
            const innerContent = (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: 0.18 + idx * 0.1, ease: "easeOut" }}
                className="relative"
              >
                {/* Numero grosso in trasparenza · ridotto su mobile */}
                <span
                  className="absolute -right-2 -bottom-3 font-editorial text-[44px] sm:text-[80px] leading-none opacity-[0.06] pointer-events-none select-none"
                  style={{ color: accent || "#7C8B6E" }}
                  aria-hidden="true"
                >
                  {idx + 1}
                </span>

                <div
                  className={`w-7 h-7 sm:w-10 sm:h-10 rounded-full flex items-center justify-center mb-1.5 sm:mb-3 mx-auto transition-transform ${onClick ? "group-hover:scale-105" : ""}`}
                  style={{ background: `${accent || "#7C8B6E"}1A`, color: accent || "#7C8B6E" }}
                >
                  {Icon ? <Icon size={14} weight="bold" className="sm:hidden" /> : null}
                  {Icon ? <Icon size={18} weight="bold" className="hidden sm:block" /> : null}
                </div>

                <div
                  className="text-[10px] sm:text-[14px] uppercase tracking-[0.12em] font-bold mb-0.5 sm:mb-1.5"
                  style={{ color: accent || "#7C8B6E" }}
                >
                  {stepOverline}
                </div>

                <div className="text-[12px] sm:text-[14px] font-medium text-[#1C1C1A] leading-snug mb-0.5 sm:mb-1.5">
                  {stepTitle}
                </div>
                <div className="text-[10.5px] sm:text-[11.5px] text-[#6B6A65] leading-snug">
                  {body}
                </div>

                {onClick && (
                  <div className="mt-1.5 sm:mt-3 inline-flex items-center gap-1 text-[10.5px] sm:text-[11.5px] font-semibold text-[#7C8B6E] group-hover:gap-1.5 transition-all">
                    Parti da qui <ArrowRight size={11} weight="bold" />
                  </div>
                )}
              </motion.div>
            );
            // iter v29 r242 — User M1399: card centrate (icona + testi).
            const wrapperClassName = `text-center bg-white border border-[#E5E7E0] rounded-lg p-2.5 sm:p-4 relative overflow-hidden block w-full ${
              onClick ? "hover:border-[#7C8B6E] active:bg-[#F8F7F2] transition-colors group cursor-pointer" : ""
            }`;
            if (onClick) {
              return (
                <button
                  key={key}
                  type="button"
                  onClick={onClick}
                  className={wrapperClassName}
                  style={{ touchAction: "manipulation", WebkitTapHighlightColor: "rgba(124,139,110,0.15)" }}
                  data-testid={`section-onb-card-${key}`}
                >
                  {innerContent}
                </button>
              );
            }
            return (
              <div
                key={key}
                className={wrapperClassName}
                data-testid={`section-onb-card-${key}`}
              >
                {innerContent}
              </div>
            );
          })}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.4 }}
          className="mt-4 text-[11px] text-[#9A968B] italic text-center"
        >
          Chiudi questa guida con la × — non la rivedrai più.
        </motion.p>
      </div>
    </DismissibleIntro>
  );
}
